import { getConsent, hasConsent, onConsentChange } from '../scripts/consent.js';
import { isDocsPage, DOCS_PRIVACY_CONFIG, docsPublicUrl } from '../scripts/developer-docs-privacy.js';
import { sanitizeDocsEvent, isDeveloperEvent } from '../scripts/developer-docs-events.js';
import { isProductionHost } from '../scripts/analytics-host.js';
import { installSignupCtaTracking } from '../scripts/signup-cta.js';
import { APP_ORIGIN } from '../scripts/app-origin.js';

export { isProductionHost };

const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

// Every website event carries app=website; the App's client registers
// app=dashboard into the same project, so one person's journey can be split by
// surface without parsing URLs.
const SOURCE_APP = 'website';

// Skip analytics entirely during local development — we only want real,
// production traffic in PostHog. isProductionHost() (SCO-650) is the stronger
// gate: only scopeworth.app and www.scopeworth.app ever start the client, so
// staging, previews and any other host stay out of the production project even
// when a bundle carries the key. This check remains for clarity. Covers Vite
// (localhost:5173) and the Cloudflare pages:dev worker (localhost:8788), plus loopback IPs and the
// .local mDNS suffix used when testing from another device on the LAN.
function isLocalhost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '0.0.0.0' ||
    h === '[::1]' ||
    h === '::1' ||
    h.endsWith('.local')
  );
}

// Query parameters that must never reach PostHog. The sign in callback lands on
// /callback.html?provider=…&email=…&name=…, and PostHog's automatic pageview
// reports the full URL in $current_url and $referrer — so without this the
// visitor's identity would be recorded despite our custom events being clean.
// Auth codes are listed too: they should never appear in a client URL, but a
// leaked credential is not the failure mode to find out about later.
const REDACTED_QUERY_PARAMS = [
  'email', 'name', 'workspace',
  'code', 'state', 'token', 'id_token', 'access_token',
  'rid', 'request_id',
];

function redactUrl(value) {
  if (typeof window !== 'undefined' && isDocsPage(window.location.pathname)) return docsPublicUrl(value, window.location.origin);
  // Only touch things that really are URLs; any other string is left alone.
  if (typeof value !== 'string') return value;
  if (!/^(https?:\/\/|\/)/.test(value) || !value.includes('?')) return value;
  try {
    const url = new URL(value, window.location.origin);
    let touched = false;
    REDACTED_QUERY_PARAMS.forEach((param) => {
      if (url.searchParams.has(param)) { url.searchParams.set(param, 'redacted'); touched = true; }
    });
    // Untouched URLs are returned verbatim so campaign parameters (utm_*, gclid)
    // survive exactly as they arrived.
    return touched ? url.toString() : value;
  } catch (_) {
    return value;
  }
}

function sanitizeProperties(properties) {
  if (!properties || typeof properties !== 'object') return properties;
  const clean = {};
  for (const [key_, value] of Object.entries(properties)) clean[key_] = redactUrl(value);
  return clean;
}

// Our own testing must never reach PostHog. A browser is marked once, by
// loading any page with ?ph_optout=1, and stays marked until ?ph_optout=0
// clears it. Marking the device rather than the network is what makes this
// hold: it survives a reassigned home IP, a VPN, and mobile data, none of
// which the INTERNAL_IPS allowlist in functions/api/_shared.js can follow.
//
// Evaluated at module load, before start() runs below, so the flag is already
// synced when start() consults isInternalDevice() — including on a first visit
// where the client now starts cookieless before any banner interaction.
const INTERNAL_KEY = 'sw_internal_traffic';

(function syncInternalFlag() {
  if (typeof window === 'undefined') return;
  try {
    const flag = new URLSearchParams(window.location.search).get('ph_optout');
    if (flag === '1') window.localStorage.setItem(INTERNAL_KEY, '1');
    else if (flag === '0') window.localStorage.removeItem(INTERNAL_KEY);
  } catch (_) { /* private window or storage blocked: nothing gets marked */ }
})();

// Also read by scripts/api-client.js: when a device is marked posthog never
// initialises, so there is no distinct ID for the server to recognise and the
// form submission would otherwise still be captured server side.
export function isInternalDevice() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(INTERNAL_KEY) === '1';
  } catch (_) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Loading. posthog-js is ~60 KB compressed and was part of every page's
// initial bundle, executing before the page was interactive. It is now a
// separate chunk fetched once the page has loaded (or the browser is idle,
// whichever is first, capped so a slow page never delays it more than a few
// seconds). Nothing above the fold depends on it: the pageview PostHog records
// on init is stamped with the page URL either way.
//
// `posthog` below is the real client once the chunk has arrived; the default
// export is a facade with the same surface the rest of the site uses
// (`__loaded`, capture, identify, get_distinct_id), which simply no-ops until
// then. Callers already guard on `__loaded`, so nothing else had to change.
// ---------------------------------------------------------------------------
let posthog = null;
let loading = null;

function load() {
  if (!loading) loading = import('posthog-js').then((m) => m.default);
  return loading;
}

// The approved website policy permits memory-only capture before a choice.
// Accepting analytics upgrades persistence and permits cross-visit identity;
// declining stops capture, and a stored decline prevents the client starting.
// This describes client behavior, not an exemption from privacy law or erasure
// of events already received by the provider. The versioned decision record
// lives in the App repository's docs/v1/privacy-decision.json.
let started = false;
let optedOut = false;
const readyListeners = new Set();
export function onAnalyticsReady(listener) { readyListeners.add(listener); if (posthog?.__loaded) listener(); return () => readyListeners.delete(listener); }
export function mayStartAnalytics() { const decision = getConsent(); return Boolean(key && host && isProductionHost() && !isLocalhost() && !isInternalDevice() && (!decision || decision.analytics) && !optedOut); }

function start() {
  if (started || !key || !host || !isProductionHost() || isLocalhost() || isInternalDevice()) return;
  // Re-checked here, not only when scheduling: the visitor may have declined in
  // the seconds between page load and this deferred start.
  const decision = getConsent();
  if (decision && !decision.analytics) return;
  started = true;
  load().then((client) => {
    if (!mayStartAnalytics()) { started = false; return; }
    const docs = isDocsPage(window.location.pathname);
    client.init(key, {
      api_host: host,
      // api_host is the t.scopeworth.app reverse proxy, so the real PostHog UI
      // origin must be named for toolbar/dashboard links to resolve.
      ui_host: 'https://eu.posthog.com',
      disable_conversations: true,
      defaults: '2026-05-30',
      persistence: hasConsent('analytics') ? 'localStorage+cookie' : 'memory',
      person_profiles: docs ? 'never' : 'identified_only',
      sanitize_properties: docs ? undefined : sanitizeProperties,
      ...(isDocsPage(window.location.pathname) ? DOCS_PRIVACY_CONFIG : {}),
      ...(docs ? { get_current_url: () => docsPublicUrl(window.location.href, window.location.origin) } : {}),
      // Registered before PostHog records the initial pageview. Docs pages keep
      // their closed property allowlist, so nothing is registered there.
      loaded: (ready) => { if (!docs) ready.register({ app: SOURCE_APP }); },
      before_send: (event) => {
        if (!docs && !isDeveloperEvent(event?.event)) {
          // The super property lives in the persistence the App shares on this
          // host; stamping here keeps a value the App wrote in another tab from
          // labelling a website event.
          if (event && event.properties) event.properties.app = SOURCE_APP;
          return event;
        }
        const safe = sanitizeDocsEvent(event, window.location.origin);
        // Analytics runs on production hosts only, so this explicit debug hook
        // does too. It logs sanitized event names, never payloads.
        if (safe && new URLSearchParams(window.location.search).get('docs_event_debug') === '1') console.info('[developer-docs event]', safe.event);
        return safe;
      },
    });
    posthog = client;
    for (const listener of readyListeners) { try { listener(); } catch {} }
  }).catch(() => { started = false; /* analytics must never break the page */ });
}

function scheduleStart() {
  if (typeof window === 'undefined') return;
  const decision = getConsent();
  if (decision && !decision.analytics) return;
  let fired = false;
  const go = () => { if (fired) return; fired = true; start(); };
  const whenIdle = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 2000 });
    else window.setTimeout(go, 400);
  };
  if (document.readyState === 'complete') whenIdle();
  else window.addEventListener('load', whenIdle, { once: true });
  // Hard cap: a page whose `load` event is held up (a slow third party, say)
  // must not postpone the pageview indefinitely.
  window.setTimeout(go, 4000);
}

scheduleStart();

// SCO-650: clicks into the App (sign-up, sign-in, dashboard) are recorded from
// one delegated listener. Docs pages keep their own closed event catalogue.
if (typeof document !== 'undefined' && !isDocsPage(window.location.pathname)) {
  installSignupCtaTracking(document, {
    location: window.location,
    appOrigin: APP_ORIGIN,
    capture: (event, properties) => {
      if (posthog && posthog.__loaded) posthog.capture(event, properties, { transport: 'sendBeacon' });
    },
  });
}

// Runs only on a real banner action, so consent_choice is recorded exactly once
// per decision — that is what makes the banner's acceptance rate measurable.
// Withdrawal of previously granted consent is followed by a storage wipe and
// reload from the banner itself; sendBeacon lets the decline event outlive it.
onConsentChange((consent) => {
  const choice = { analytics: consent.analytics === true, marketing: consent.marketing === true };
  try {
    if (consent.analytics) {
      if (posthog) {
        posthog.set_config({ persistence: 'localStorage+cookie', disable_persistence: false });
        if (optedOut) { posthog.opt_in_capturing(); optedOut = false; }
        posthog.capture('consent_choice', choice);
      } else {
        // Not loaded yet (deferred start, or a first visit that accepted before
        // the chunk arrived): start now, then record the choice once it is up.
        start();
        if (loading) loading.then(() => posthog && posthog.capture('consent_choice', choice)).catch(() => {});
      }
    } else if (posthog && !optedOut) {
      posthog.capture('consent_choice', choice, { transport: 'sendBeacon' });
      posthog.opt_out_capturing();
      // Opting out stops events but does not necessarily disable SDK storage.
      // Prevent a queued persistence/unload write restoring a cookie after the
      // consent panel clears it and reloads the page.
      posthog.set_config({ disable_persistence: true });
      optedOut = true;
    }
  } catch (_) { /* analytics must never break the page */ }
});

// The browser distinct ID is safe to pass to our same-origin Pages Function.
// The server uses it to keep the lead-submission event on the same journey as
// the page views/session activity that preceded the form submission.
export function getDistinctId() {
  try {
    if (!posthog || !posthog.__loaded || typeof posthog.get_distinct_id !== 'function') return null;
    const id = posthog.get_distinct_id();
    return typeof id === 'string' && id.length > 0 && id.length <= 200 ? id : null;
  } catch (_) {
    return null;
  }
}

// Same shape the call sites relied on when this module re-exported posthog-js
// itself: `__loaded` is false until the client is initialised, and the methods
// are no-ops until then.
const facade = {
  get __loaded() { return Boolean(posthog && posthog.__loaded); },
  capture(...args) { return posthog ? posthog.capture(...args) : undefined; },
  identify(...args) { return posthog && !isDocsPage(window.location.pathname) ? posthog.identify(...args) : undefined; },
  get_distinct_id() { return posthog ? posthog.get_distinct_id() : undefined; },
};

export default facade;
